import React, { useEffect, useState, useCallback } from "react";
import PopupNotification from "./ui/PopupNotification";
import AnnouncementNotificationDialog from "./AnnouncementNotificationDialog";
import MarketingNotificationDialog from "./MarketingNotificationDialog";
import { NotificationTypeEnum } from "../modules/shared/model/user-notification.enum";

interface NotificationData {
  notificationId: string;
  title: string;
  message: string;
  priority?: {
    id: number;
    name: string;
  };
  type?: {
    id: number;
    name: string;
  };
  url?: string | null;
  expiryTime?: string | null;
  status?: {
    id: number;
    name: string;
  };
  createdAt?: string;
  popupData?: any;
}

export interface PopupNotificationState extends NotificationData {
  id: string;
}

const NotificationManager: React.FC = () => {
  const [popupNotifications, setPopupNotifications] = useState<PopupNotificationState[]>([]);
  const [announcementNotifications, setAnnouncementNotifications] = useState<PopupNotificationState[]>([]);
  const [marketingNotifications, setMarketingNotifications] = useState<PopupNotificationState[]>([]);

  const handleClosePopup = useCallback((id: string) => {
    setPopupNotifications((prev) => prev.filter((popup) => popup.id !== id));
  }, []);

  const handleCloseAnnouncement = useCallback((id: string) => {
    setAnnouncementNotifications((prev) => prev.filter((announcement) => announcement.id !== id));
  }, []);

  const handleCloseMarketing = useCallback((id: string) => {
    setMarketingNotifications((prev) => prev.filter((marketing) => marketing.id !== id));
  }, []);

  // Listen for custom event to show notifications
  useEffect(() => {
    const handleShowPopup = (event: CustomEvent<PopupNotificationState>) => {
      const notification = event.detail;
      const typeId = Number(notification.type?.id);  // Parse as number
      
      // Route to appropriate notification type
      if (typeId === NotificationTypeEnum.Announcement) {
        setAnnouncementNotifications((prev) => [...prev, notification]);
        
        // Auto-remove after 30 seconds
        setTimeout(() => {
          handleCloseAnnouncement(notification.id);
        }, 30000);
      } else if (typeId === NotificationTypeEnum.Marketing) {
        setMarketingNotifications((prev) => [...prev, notification]);
        
        // Auto-remove after 30 seconds
        setTimeout(() => {
          handleCloseMarketing(notification.id);
        }, 30000);
      } else if (typeId === NotificationTypeEnum.Popup || typeId === NotificationTypeEnum["In-App"]) {
        setPopupNotifications((prev) => [...prev, notification]);
        
        // Auto-remove after 30 seconds
        setTimeout(() => {
          handleClosePopup(notification.id);
        }, 30000);
      }
    };

    window.addEventListener("showNotificationPopup" as any, handleShowPopup);

    return () => {
      window.removeEventListener("showNotificationPopup" as any, handleShowPopup);
    };
  }, [handleClosePopup, handleCloseAnnouncement, handleCloseMarketing]);

  return (
    <>
      {/* Popup and In-App Notifications */}
      {popupNotifications.length > 0 && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 9999 }}>
          {popupNotifications.map((popup) => (
            <PopupNotification
              key={popup.id}
              id={popup.id}
              title={popup.title}
              message={popup.message}
              priority={popup.priority}
              type={popup.type}
              url={popup.url}
              expiryTime={popup.expiryTime}
              onClose={handleClosePopup}
            />
          ))}
        </div>
      )}

      {/* Announcement Notifications */}
      {announcementNotifications.map((announcement) => (
        <AnnouncementNotificationDialog
          key={announcement.id}
          id={announcement.id}
          notificationId={announcement.notificationId}
          title={announcement.title}
          message={announcement.message}
          priority={announcement.priority}
          type={announcement.type}
          onClose={handleCloseAnnouncement}
        />
      ))}

      {/* Marketing Notifications */}
      {marketingNotifications.map((marketing) => {
        // Parse priority to string format
        const priorityString = marketing.priority?.id === 3 ? "high" : 
                              marketing.priority?.id === 1 ? "low" : "medium";
        
        return (
          <MarketingNotificationDialog
            key={marketing.id}
            id={marketing.id}
            notificationId={marketing.notificationId}
            title={marketing.title}
            message={marketing.message}
            priority={priorityString}
            popupData={marketing.popupData}
            onClose={() => handleCloseMarketing(marketing.id)}
          />
        );
      })}
    </>
  );
};

export default NotificationManager;

